// src/ts/main.ts
// This is the main application orchestrator.
// It determines the user's authentication status and loads the appropriate
// set of JavaScript modules, acting as the primary gate for code-splitting.
// It includes an allowlist for pages that should load JS for guest users (e.g., login).

import { getAuthData } from "./lib/api-client";
import { appConfig } from "./config";
import { initializeBaseComponents, initializeDynamicComponents } from "./components/components";

/**
 * A list of page modules that should be loaded even for GUEST users.
 * This is primarily for authentication pages like login, register, or success pages.
 */
const GUEST_ACCESSIBLE_PAGES = [
  "login",
  "success",
  "news",
  "pp",
  "quests",
  "top-partisans",
  "noble-titles",
  "game-levels",
  "quest-types",
  "quest-details",
  "all-factions",
  "factions",
  "user-ether-mobile", // MetaMask handoff - auth happens via wallet signature
  "user-ego-mobile", // MetaMask handoff for EGO - auth happens via wallet signature
];

/**
 * Dynamically loads and initializes the module for the CURRENT page,
 * using the `data-page` attribute on the `<body>` tag.
 * This is now called for authenticated users OR for pages on the guest allowlist.
 */
async function loadPageModule(pageName: string): Promise<void> {
  try {
    console.log(`Loading page module for: ${pageName}`);
    const pageModule = await import(`./pages/${pageName}.ts`);

    if (pageModule && typeof pageModule.init === "function") {
      console.log(`Module for '${pageName}' loaded. Initializing...`);
      await pageModule.init(); // We can pass args here if needed in the future
    } else {
      console.warn(`Module for page '${pageName}' is missing an export 'init' function.`);
    }
  } catch (error) {
    console.log(`No page module found for '${pageName}'. This may be intentional.`);
  }
}

/**
 * Initializes the header component for all users.
 * For authenticated users: loads full header with user data.
 * For guest users: shows CTA button and initializes Google One Tap.
 */
async function initializeHeaderFeatures(): Promise<void> {
  console.log("Initializing header features...");
  try {
    const headerModule = await import("./components/header");
    await headerModule.initializeHeader(appConfig);
  } catch (error) {
    console.error("Failed to initialize header features", error);
  }
}

// --- Application Entry Point ---
async function main() {
  initializeBaseComponents();

  const authData = getAuthData();
  const pageName = document.body.dataset.page;
  const ctaButton = document.getElementById("ctaButton");

  if (authData) {
    // Authenticated user: ensure CTA button stays hidden
    if (ctaButton) {
      ctaButton.classList.add("hide");
    }

    // Immediately preload and set avatar from authData to prevent flashing
    const userAvatar = document.getElementById("userpicButton") as HTMLImageElement | null;

    if (authData.userpic && userAvatar) {
      const img = new Image();
      img.onload = () => {
        userAvatar.src = authData.userpic;
        userAvatar.classList.remove("hide");
      };
      img.onerror = () => {
        console.warn("Failed to preload userpic image, using default.");
        userAvatar.src = "/assets/images/icons/mask.svg";
        userAvatar.classList.remove("hide");
      };
      img.src = authData.userpic;
    }
  } else {
    // Guest user: explicitly show CTA button
    if (ctaButton) {
      ctaButton.classList.remove("hide");
    }
  }

  // Initialize header for ALL users (auth header for logged-in, One Tap for guests)
  await initializeHeaderFeatures();

  if (pageName && (authData || GUEST_ACCESSIBLE_PAGES.includes(pageName))) {
    await loadPageModule(pageName);
  }

  initializeDynamicComponents();

  console.log("Main application flow complete.");
}

// Start the application once the DOM is ready.
document.addEventListener("DOMContentLoaded", main);
